<!-- TextWithNotes.vue -->
<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useSettingStore } from "@/stores/useSettingStore";
import { useNoteStore } from "@/stores/useNoteStore";
import { useTagStore } from "@/stores/useTagStore";
import { useUserStore } from "@/stores/useUserStore";
// ── NOVO: imports para "Editar Nota" no menu de contexto ─────────────────────
import { useManagedAccountStore } from "@/stores/useManagedAccountStore";
import EditNoteForm from "@/components/Note/EditNoteForm.vue";
import EditTagForm from "@/components/Tag/EditTagForm.vue";
import SettingsModal from "@/components/Setting/SettingsModal.vue";
// ── NOVO: pilha flutuante com os avatares dos criadores das notas visíveis ───
import NoteCreatorsStack from "./NoteCreatorsStack.vue";
import { compareNotesByTextOrder } from "@/utils/noteOrdering";

const settingStore = useSettingStore();
const noteStore = useNoteStore();
const tagStore = useTagStore();
const userStore = useUserStore();
// ── NOVO: store do vínculo Membro-Titular ────────────────────────────────────
const managedAccountStore = useManagedAccountStore();

const props = defineProps({
  content: { type: String, required: true },
  notes: { type: Array, default: () => [] },

  userId: { type: Number, required: true },
  createdByUserId: { type: Number, required: true },
  updatedByUserId: { type: Number, required: true },

  hasExponent: { type: Boolean, default: true },
  hasHighlights: { type: Boolean, default: true },
});

const emit = defineEmits([
  "selection-made",
  "note-clicked",
  "notes-focused",
  "note-details",
  "request-delete",
]);

const containerRef = ref(null);

// ── NOVO: âncora da pilha de criadores ───────────────────────────────────────
// A raiz deste componente é um <span> inline dentro de <p class="text-body">, e
// por isso não pode ancorar um filho posicionado. A pilha usa o mesmo container
// de rolagem já usado pelo auto-scroll do highlightTextNoteId.
const scrollAnchorEl = ref(null);

const showSettingsModal = ref(false);

watch(
  () => props.notes,
  (newNotes) => {
    console.log(
      "Novas notas recebidas:",
      Array.isArray(newNotes) ? newNotes.length : 0
    );
  },
  { deep: true, immediate: true }
);

// ── Clique esquerdo ──────────────────────────────────────────────────────────
// Clique no EXPOENTE: ele já aponta para uma nota específica, então é sempre
// "localize esta ficha na lista" — o NoteList rola até ela e ilumina.
function onNoteClick(notes) {
  if (notes && notes.length > 0) {
    const topNote = notes[0];
    if (topNote && topNote.noteId) {
      emit("note-clicked", topNote.noteId);
    }
  }
}

// Clique no TRECHO destacado. A segmentação fatia o texto em cada fronteira de
// range, então o segmento clicado carrega exatamente as notas que cobrem aquele
// ponto — e é o tamanho dessa cobertura que decide o comportamento:
//
//  • uma nota  → não há ambiguidade a resolver. Mesmo caminho do expoente:
//    localiza a ficha na lista completa, rolando e iluminando.
//  • duas ou mais (interseção de ranges) → aí sim não dá para adivinhar qual
//    delas o leitor quis, e a lista passa a exibir só as envolvidas.
function onSegmentClick(notes) {
  if (!notes || notes.length === 0) return;

  const noteIds = notes
    .map((note) => note?.noteId)
    .filter((noteId) => noteId !== null && noteId !== undefined);

  if (noteIds.length === 0) return;

  if (noteIds.length === 1) {
    emit("note-clicked", noteIds[0]);
    return;
  }

  emit("notes-focused", noteIds);
}

// ── Menu de contexto ─────────────────────────────────────────────────────────
const contextMenu = ref({
  visible: false,
  x: 0,
  y: 0,
  notes: [],
  canEditTag: false,
  tagId: null,
  // ── NOVO: campos para o item "Editar Nota" ──
  canEditNote: false,
  noteId: null,
  // ── NOVO: campos para o item "Apagar Nota" ──
  canDeleteNote: false,
});

const showEditTagForm = ref(false);

// ── NOVO: estado do EditNoteForm aberto pelo menu de contexto ────────────────
const showEditNoteForm = ref(false);
const selectedNoteIdForEdit = ref(null);

// ── NOVO: permissões efetivas do vínculo Membro-Titular para notas ───────────
const memberNotePermissions = computed(
  () => managedAccountStore.currentMemberPermissions?.notes ?? null
);
const memberCanEditNote = computed(
  () => memberNotePermissions.value === null || memberNotePermissions.value.edit
);
// ── NOVO: guard de delete espelhado do NoteCrudDropdown ──────────────────────
const memberCanDeleteNote = computed(
  () =>
    memberNotePermissions.value === null || memberNotePermissions.value.delete
);

function closeContextMenu() {
  contextMenu.value.visible = false;
}

async function onNoteRightClick(notes, event) {
  // Se já existe uma seleção de texto e o clique direito ocorreu dentro dela,
  // o menu nativo do navegador deve prevalecer. Não chama preventDefault().
  if (isRightClickInsideCurrentTextSelection(event)) {
    closeContextMenu();
    return;
  }

  event.preventDefault();

  // Quando o botão direito foi usado para arrastar/selecionar texto,
  // o contextmenu seguinte precisa morrer aqui para não abrir menu nenhum.
  if (suppressNextContextMenu.value || rightButtonSelection.value.moved) {
    event.stopPropagation();
    closeContextMenu();
    return;
  }

  if (!notes || notes.length === 0) return;

  // Fecha menu anterior imediatamente
  contextMenu.value.visible = false;

  const topNote = notes[0];

  // Determina a tag primária da nota para verificar permissão
  const tagIds = topNote?.tagIds ?? [];
  const tagIsPrimary = topNote?.tagIsPrimary ?? [];
  let primaryTagId = null;

  if (tagIds.length > 0) {
    const primaryIdx = tagIsPrimary.findIndex((v) => v === true);
    primaryTagId = tagIds[primaryIdx >= 0 ? primaryIdx : 0] ?? null;
  }

  // Verifica permissão de edição da tag (busca leve — só se houver tag)
  let canEditTag = false;
  if (primaryTagId) {
    try {
      const tag = await tagStore.getTagById(primaryTagId);
      const logged = (userStore.currentUser?.userName ?? "")
        .trim()
        .toLowerCase();
      if (logged === "sys") {
        canEditTag = true;
      } else {
        canEditTag = tag?.createdByUserId === userStore.currentUser?.userId;
      }
    } catch {
      canEditTag = false;
    }
  }

  // ── NOVO: calcula permissão de edição da nota (mesma lógica do NoteCrudDropdown) ──
  // canShowEditNote = memberCanEditNote && (isNoteCreator || isSysUser || canEditNote)
  const currentUserId = userStore.currentUser?.userId ?? null;
  const isNoteCreator = currentUserId === topNote?.createdByUserId;
  const isSysUser = (userStore.currentUser?.userName ?? "") === "Sys";
  const noteCanEditFlag = !!topNote?.canEditNote;
  const canShowEditNote =
    memberCanEditNote.value && (isNoteCreator || isSysUser || noteCanEditFlag);

  // ── NOVO: canShowDelete espelha NoteCrudDropdown ──────────────────────────
  // memberCanDeleteNote && (canShowEditNote || isActivityCreator || isTextCreator)
  // TextWithNotes não tem acesso a activityCreatedByUserId/textCreatedByUserId,
  // mas tem createdByUserId (do texto). Espelhamos com o que está disponível.
  const isTextCreator = currentUserId === props.createdByUserId;
  const canShowDelete =
    memberCanDeleteNote.value && (canShowEditNote || isTextCreator);

  // Posiciona o menu onde o mouse clicou
  // Aguarda nextTick para garantir que o menu seja renderizado antes de ajustar posição
  contextMenu.value = {
    visible: true,
    x: event.clientX,
    y: event.clientY,
    notes,
    canEditTag,
    tagId: primaryTagId,
    // ── NOVO: dados da nota para o item "Editar Nota" ──
    canEditNote: canShowEditNote,
    noteId: topNote?.noteId ?? null,
    // ── NOVO: permissão de apagar ──
    canDeleteNote: canShowDelete,
  };

  await nextTick();
  adjustMenuPosition();
}

function adjustMenuPosition() {
  const menu = document.getElementById("twn-context-menu");
  if (!menu) return;

  const menuRect = menu.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let x = contextMenu.value.x;
  let y = contextMenu.value.y;

  // Evita que o menu saia pela direita
  if (x + menuRect.width > vw - 8) {
    x = vw - menuRect.width - 8;
  }
  // Evita que o menu saia pela parte inferior
  if (y + menuRect.height > vh - 8) {
    y = vh - menuRect.height - 8;
  }

  contextMenu.value.x = x;
  contextMenu.value.y = y;
}

function handleContextMenuOpenSettings() {
  closeContextMenu();
  showSettingsModal.value = true;
}

function handleContextMenuDetails() {
  const notes = contextMenu.value.notes;
  closeContextMenu();
  if (notes && notes.length > 0) {
    const topNote = notes[0];
    if (topNote?.noteId) {
      emit("note-details", topNote.noteId);
    }
  }
}

function handleContextMenuEditTag() {
  if (!contextMenu.value.canEditTag || !contextMenu.value.tagId) return;
  tagStore.setIdTag(contextMenu.value.tagId);
  closeContextMenu();
  showEditTagForm.value = true;
}

// ── NOVO: abre o EditNoteForm com a nota do menu de contexto ─────────────────
function handleContextMenuEditNote() {
  if (!contextMenu.value.canEditNote || !contextMenu.value.noteId) return;
  selectedNoteIdForEdit.value = contextMenu.value.noteId;
  closeContextMenu();
  showEditNoteForm.value = true;
}

function handleEditNoteClose() {
  showEditNoteForm.value = false;
  selectedNoteIdForEdit.value = null;
}

// ── NOVO: emite request-delete para o pai (TextDisplayer) tratar ─────────────
function handleContextMenuDeleteNote() {
  if (!contextMenu.value.canDeleteNote || !contextMenu.value.noteId) return;
  const noteId = contextMenu.value.noteId;
  closeContextMenu();
  emit("request-delete", noteId);
}

function handleEditTagRefresh() {
  // EditTagForm já chama tagStore.updateTag que propaga para allNotes/notes
  // Nada adicional necessário aqui
}

// Fecha o menu ao clicar fora ou pressionar Escape
function handleGlobalClick(event) {
  const menu = document.getElementById("twn-context-menu");
  if (menu && !menu.contains(event.target)) {
    closeContextMenu();
  }
}

function handleGlobalKeydown(event) {
  if (event.key === "Escape") {
    closeContextMenu();
  }
}

// ── Seleção de texto com mouse ───────────────────────────────────────────────
const LEFT_BUTTON = 0;
const LEFT_BUTTON_MASK = 1;
const RIGHT_BUTTON = 2;
const RIGHT_BUTTON_MASK = 2;
const LEFT_DRAG_THRESHOLD_PX = 4;
const RIGHT_DRAG_THRESHOLD_PX = 4;
let releaseContextMenuSuppressionTimer = null;
let releaseSelectionMadeSuppressionTimer = null;

const suppressNextContextMenu = ref(false);
const suppressNextSelectionMade = ref(false);

const leftButtonSelection = ref({
  active: false,
  moved: false,
  startX: 0,
  startY: 0,
});

const rightButtonSelection = ref({
  active: false,
  moved: false,
  startX: 0,
  startY: 0,
  anchorRange: null,
});

function getCaretRangeFromPoint(x, y) {
  if (typeof document.caretRangeFromPoint === "function") {
    return document.caretRangeFromPoint(x, y);
  }

  if (typeof document.caretPositionFromPoint === "function") {
    const position = document.caretPositionFromPoint(x, y);
    if (!position) return null;

    const range = document.createRange();
    range.setStart(position.offsetNode, position.offset);
    range.collapse(true);
    return range;
  }

  return null;
}

function isRangeInsideContainer(range) {
  return !!(
    range &&
    containerRef.value &&
    containerRef.value.contains(range.startContainer)
  );
}

function isPointInsideRangeClientRects(range, x, y) {
  if (!range) return false;

  const rects = range.getClientRects();
  for (const rect of rects) {
    if (
      x >= rect.left &&
      x <= rect.right &&
      y >= rect.top &&
      y <= rect.bottom
    ) {
      return true;
    }
  }

  return false;
}

function isRightClickInsideCurrentTextSelection(event) {
  if (!event || event.button !== RIGHT_BUTTON) return false;

  const selection = window.getSelection();
  if (
    !selection ||
    selection.rangeCount === 0 ||
    selection.isCollapsed ||
    !selection.toString().trim()
  ) {
    return false;
  }

  for (let index = 0; index < selection.rangeCount; index += 1) {
    const range = selection.getRangeAt(index);

    if (!containerRef.value || !range.intersectsNode(containerRef.value)) {
      continue;
    }

    if (isPointInsideRangeClientRects(range, event.clientX, event.clientY)) {
      return true;
    }
  }

  return false;
}

function resetLeftButtonSelection() {
  leftButtonSelection.value = {
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
  };
}

function resetRightButtonSelection() {
  rightButtonSelection.value = {
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    anchorRange: null,
  };
}

function temporarilySuppressContextMenu() {
  suppressNextContextMenu.value = true;

  if (releaseContextMenuSuppressionTimer) {
    clearTimeout(releaseContextMenuSuppressionTimer);
  }

  releaseContextMenuSuppressionTimer = window.setTimeout(() => {
    suppressNextContextMenu.value = false;
    releaseContextMenuSuppressionTimer = null;
  }, 350);
}

function temporarilySuppressSelectionMade() {
  suppressNextSelectionMade.value = true;

  if (releaseSelectionMadeSuppressionTimer) {
    clearTimeout(releaseSelectionMadeSuppressionTimer);
  }

  releaseSelectionMadeSuppressionTimer = window.setTimeout(() => {
    suppressNextSelectionMade.value = false;
    releaseSelectionMadeSuppressionTimer = null;
  }, 500);
}

function clearSelectionMadeSuppression() {
  suppressNextSelectionMade.value = false;

  if (releaseSelectionMadeSuppressionTimer) {
    clearTimeout(releaseSelectionMadeSuppressionTimer);
    releaseSelectionMadeSuppressionTimer = null;
  }
}

function handleTextMouseDown(event) {
  if (event.button === LEFT_BUTTON) {
    resetRightButtonSelection();

    leftButtonSelection.value = {
      active: true,
      moved: false,
      startX: event.clientX,
      startY: event.clientY,
    };

    return;
  }

  resetLeftButtonSelection();

  if (event.button !== RIGHT_BUTTON) return;

  const anchorRange = getCaretRangeFromPoint(event.clientX, event.clientY);
  if (!isRangeInsideContainer(anchorRange)) {
    resetRightButtonSelection();
    return;
  }

  closeContextMenu();
  rightButtonSelection.value = {
    active: true,
    moved: false,
    startX: event.clientX,
    startY: event.clientY,
    anchorRange: anchorRange.cloneRange(),
  };
}

function updateRightButtonTextSelection(focusRange) {
  const anchorRange = rightButtonSelection.value.anchorRange;
  if (!anchorRange || !isRangeInsideContainer(focusRange)) return;

  try {
    const nextRange = document.createRange();
    const anchorBeforeFocus =
      anchorRange.compareBoundaryPoints(Range.START_TO_START, focusRange) <= 0;

    if (anchorBeforeFocus) {
      nextRange.setStart(anchorRange.startContainer, anchorRange.startOffset);
      nextRange.setEnd(focusRange.startContainer, focusRange.startOffset);
    } else {
      nextRange.setStart(focusRange.startContainer, focusRange.startOffset);
      nextRange.setEnd(anchorRange.startContainer, anchorRange.startOffset);
    }

    const selection = window.getSelection();
    if (!selection) return;

    selection.removeAllRanges();
    selection.addRange(nextRange);
  } catch {
    // Evita quebrar o componente se o browser retornar um ponto de caret inválido.
  }
}

function handleLeftButtonMouseMove(event) {
  if (!leftButtonSelection.value.active) return;

  if ((event.buttons & LEFT_BUTTON_MASK) !== LEFT_BUTTON_MASK) {
    resetLeftButtonSelection();
    return;
  }

  const deltaX = Math.abs(event.clientX - leftButtonSelection.value.startX);
  const deltaY = Math.abs(event.clientY - leftButtonSelection.value.startY);

  if (
    !leftButtonSelection.value.moved &&
    deltaX < LEFT_DRAG_THRESHOLD_PX &&
    deltaY < LEFT_DRAG_THRESHOLD_PX
  ) {
    return;
  }

  leftButtonSelection.value.moved = true;
}

function handleRightButtonMouseMove(event) {
  if (!rightButtonSelection.value.active) return;

  if ((event.buttons & RIGHT_BUTTON_MASK) !== RIGHT_BUTTON_MASK) {
    resetRightButtonSelection();
    return;
  }

  const deltaX = Math.abs(event.clientX - rightButtonSelection.value.startX);
  const deltaY = Math.abs(event.clientY - rightButtonSelection.value.startY);

  if (
    !rightButtonSelection.value.moved &&
    deltaX < RIGHT_DRAG_THRESHOLD_PX &&
    deltaY < RIGHT_DRAG_THRESHOLD_PX
  ) {
    return;
  }

  rightButtonSelection.value.moved = true;
  temporarilySuppressContextMenu();
  temporarilySuppressSelectionMade();
  closeContextMenu();
  event.preventDefault();

  const focusRange = getCaretRangeFromPoint(event.clientX, event.clientY);
  updateRightButtonTextSelection(focusRange);
}

function handleRightButtonMouseUp(event) {
  if (!rightButtonSelection.value.active) return;

  const wasRightButtonDrag = rightButtonSelection.value.moved;
  resetRightButtonSelection();

  if (!wasRightButtonDrag) return;

  temporarilySuppressContextMenu();
  temporarilySuppressSelectionMade();
  closeContextMenu();
  event.preventDefault();
}

function handleGlobalContextMenu(event) {
  if (!suppressNextContextMenu.value) return;

  event.preventDefault();
  event.stopPropagation();
  closeContextMenu();
}

// ── Helpers de escape e word detection ──────────────────────────────────────
function escapeHtml(text) {
  return (text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isSingleWord(text) {
  return text?.trim().split(/\s+/).length === 1;
}

// ── Cores das tags ───────────────────────────────────────────────────────────
const fallbackColors = computed(() => {
  const s = settingStore?.lastConsultedSetting;
  return {
    text: s?.uncategorizedTextColor || "#000000",
    bg: s?.uncategorizedBackgroundColor || "#ffffff",
    underline: s?.uncategorizedUnderlineColor || "#000000",
  };
});

function getPrimaryTagIndex(note) {
  const arr = note?.tagIsPrimary;
  if (!Array.isArray(arr) || arr.length === 0) return 0;
  const idx = arr.findIndex((v) => v === true);
  return idx >= 0 ? idx : 0;
}

function pickSpecific(arr, primaryIndex, tagIdsLength) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  if (
    typeof tagIdsLength === "number" &&
    tagIdsLength > 0 &&
    arr.length === tagIdsLength
  ) {
    return arr[primaryIndex] ?? null;
  }
  return arr[0] ?? null;
}

function getNoteVisuals(note) {
  const primaryIndex = getPrimaryTagIndex(note);
  const tagIdsLength = Array.isArray(note?.tagIds) ? note.tagIds.length : 0;

  const specificTextColor = pickSpecific(
    note?.tagSpecificColors,
    primaryIndex,
    tagIdsLength
  );
  const specificBgColor = pickSpecific(
    note?.tagSpecificBackgrounds,
    primaryIndex,
    tagIdsLength
  );
  const specificUnderline = pickSpecific(
    note?.tagSpecificUnderlines,
    primaryIndex,
    tagIdsLength
  );

  const defaults = fallbackColors.value;

  return {
    textColor: specificTextColor || note?.defaultColor || defaults.text,
    bgColor: specificBgColor || note?.defaultBackground || defaults.bg,
    underlineColor:
      specificUnderline || note?.defaultUnderline || defaults.underline,
  };
}

// ── Segmentação e highlight ──────────────────────────────────────────────────
const MAX_SEGMENT_TEXT_LENGTH = 4000;

const highlightedSegments = computed(() => {
  const rawNotes = Array.isArray(props.notes) ? props.notes : [];

  const validNotes = rawNotes.filter((note) => {
    const start = note.textRangeStart;
    const end = note.textRangeEnd;
    if (typeof start !== "number" || typeof end !== "number") return false;
    if (start === 0 && end === 0) return false;
    if (start < 0 || end < 0 || start >= end) return false;
    if (start >= props.content.length) return false;
    return true;
  });

  const processedNotes = validNotes.map((note) => {
    const originalTextSegment = props.content.slice(
      note.textRangeStart,
      note.textRangeEnd
    );
    return { ...note, isOriginalSingleWord: isSingleWord(originalTextSegment) };
  });

  const events = [];
  const endReferenceEvents = [];

  for (const note of processedNotes) {
    events.push({ pos: note.textRangeStart, note, action: "start" });
    const endEvent = { pos: note.textRangeEnd, note, action: "end" };
    events.push(endEvent);
    endReferenceEvents.push(endEvent);
  }

  // A numeração segue o INÍCIO do trecho: entre duas notas, recebe o número
  // menor a que começa antes. Num trecho aninhado (a = 1–100 contendo
  // b = 50–70), `a` é 1 e `b` é 2.
  //
  // ⚠️ O expoente continua sendo DESENHADO no fim do trecho, então nesse caso o
  // `2` aparece na tela antes do `1`. Numerar pelo começo e marcar no fim são
  // decisões independentes; esta é a do começo.
  //
  // O comparador é compartilhado com o renderizador HTML e com a store — ver
  // `noteOrdering.js` para por que ele não pode divergir.
  endReferenceEvents.sort((a, b) => compareNotesByTextOrder(a.note, b.note));
  const noteExponentMap = new Map();
  endReferenceEvents.forEach((event, index) => {
    noteExponentMap.set(event.note, index + 1);
  });

  // Ordem: posição; em empate, "end" antes de "start" (uma nota que termina no
  // offset X fecha antes de outra que começa ali); em empate total, a ordem de
  // inserção — Array.prototype.sort é estável desde a ES2019, e devolver 0 aqui
  // preserva a ordem de props.notes.
  //
  // A comparação precisa olhar as DUAS ações. A versão anterior devolvia -1
  // sempre que `a` era um "end", sem consultar `b`: para dois "end" no mesmo
  // offset, c(a,b) e c(b,a) afirmavam ambos "vem antes", e o resultado prático
  // era a inversão — duas notas sobre o mesmo trecho saíam como "2, 1".
  //
  // A terceira chave só decide entre dois "end" no MESMO offset, e existe para
  // as marcas coladas ("1, 2") saírem em ordem crescente: com a numeração pelo
  // início, duas notas que terminam juntas podem ter números fora de sequência.
  // Reordenar dois "end" é seguro — remover de um Set não mexe na ordem do que
  // fica. Fazer o mesmo com dois "start" mudaria a ordem de `activeNotes`, e com
  // ela qual nota fica por cima no realce.
  const actionRank = (action) => (action === "end" ? 0 : 1);

  events.sort(
    (a, b) =>
      a.pos - b.pos ||
      actionRank(a.action) - actionRank(b.action) ||
      (a.action === "end" ? compareNotesByTextOrder(a.note, b.note) : 0)
  );

  const segments = [];
  const activeNotes = new Set();
  let lastPos = 0;

  for (const event of events) {
    if (event.pos > lastPos) {
      segments.push({
        text: props.content.slice(lastPos, event.pos),
        start: lastPos,
        notes: Array.from(activeNotes),
        exponent: null,
      });
    }

    if (event.action === "end") {
      const exponentNumber = noteExponentMap.get(event.note);
      segments.push({
        text: "",
        start: event.pos,
        notes: [event.note],
        exponent: exponentNumber,
      });
      activeNotes.delete(event.note);
    } else if (event.action === "start") {
      activeNotes.add(event.note);
    }
    lastPos = event.pos;
  }

  if (lastPos < props.content.length) {
    segments.push({
      text: props.content.slice(lastPos),
      start: lastPos,
      notes: Array.from(activeNotes),
      exponent: null,
    });
  }

  const styledSegments = segments.map((seg) => {
    const styleParts = [];
    const hasNotes = seg.notes.length > 0;

    // Segmentos de texto vazio servem apenas como âncora do expoente; não devem
    // receber fundo/padding (senão geram um "nó" colorido extra no fim do trecho).
    if (hasNotes && props.hasHighlights && seg.text) {
      // Notas que cobrem este segmento, separadas pela origem (range original).
      const singleNote = seg.notes.find((n) => n.isOriginalSingleWord) || null;
      const multiNote = seg.notes.find((n) => !n.isOriginalSingleWord) || null;

      // ── Fundo do trecho (multi-palavra) ───────────────────────────────────
      // Aplicado de forma INDEPENDENTE, para que possa coexistir (sobrepor-se)
      // com a estilização de palavra única quando há interseção de ranges.
      if (multiNote) {
        const { bgColor } = getNoteVisuals(multiNote);
        styleParts.push(`background-color: ${bgColor};`);

        // Arredondamento e padding SOMENTE nas extremidades reais do trecho.
        // Nas junções internas (onde o trecho foi fatiado por uma palavra única
        // que o intercepta) o fundo fica reto, garantindo continuidade visual —
        // sem "degraus" nem cantos arredondados no meio do trecho.
        const segEnd = seg.start + (seg.text ? seg.text.length : 0);
        const isTrechoStart = seg.start === multiNote.textRangeStart;
        const isTrechoEnd = segEnd === multiNote.textRangeEnd;

        if (isTrechoStart) {
          styleParts.push(
            "border-top-left-radius: 0.5rem; border-bottom-left-radius: 0.5rem; padding-left: 0.25rem;"
          );
        }
        if (isTrechoEnd) {
          styleParts.push(
            "border-top-right-radius: 0.5rem; border-bottom-right-radius: 0.5rem; padding-right: 0.25rem;"
          );
        }
      }

      // ── Palavra única ──────────────────────────────────────────────────────
      // Apenas cor do texto + negrito. SOBREPÕE-SE ao fundo do trecho quando
      // ambos cobrem o mesmo segmento (interseção), em vez de suprimi-lo.
      if (singleNote) {
        const { textColor } = getNoteVisuals(singleNote);
        styleParts.push(`color: ${textColor}; font-weight: bold;`);
      }

      styleParts.push("cursor: pointer;");
    }

    return {
      text: seg.text,
      start: seg.start,
      classList: "",
      style: styleParts.join(" "),
      exponent: seg.exponent,
      notes: seg.notes,
      hasNotes,
      // Ids das notas deste segmento, para o leitor saber quais estão na tela
      // sem varrer a árvore: o IntersectionObserver do TextDisplayer observa só
      // os spans que TÊM nota. `undefined` faz o Vue omitir o atributo, então
      // segmento sem nota não é observado.
      noteIdsAttr: hasNotes
        ? seg.notes.map((note) => note.noteId).join(",")
        : undefined,
    };
  });

  const chunkedSegments = [];
  for (const seg of styledSegments) {
    const text = seg.text || "";
    if (!text.length || text.length <= MAX_SEGMENT_TEXT_LENGTH) {
      chunkedSegments.push(seg);
      continue;
    }
    let localOffset = 0;
    while (localOffset < text.length) {
      const slice = text.slice(
        localOffset,
        localOffset + MAX_SEGMENT_TEXT_LENGTH
      );
      chunkedSegments.push({
        ...seg,
        text: slice,
        start: seg.start + localOffset,
      });
      localOffset += MAX_SEGMENT_TEXT_LENGTH;
    }
  }

  // Marca expoentes que são continuação de uma sequência (notas que terminam no
  // mesmo ponto de término), para o template renderizar o Formato B "1, 2, 3":
  // vírgula + espaço ENTRE expoentes, sem parênteses e sem espaços antes/depois
  // da sequência. Como segmentos de expoente têm texto vazio, dois expoentes
  // consecutivos no array indicam o mesmo ponto de término.
  for (let i = 0; i < chunkedSegments.length; i += 1) {
    const current = chunkedSegments[i];
    current.exponentIsContinuation =
      !!current.exponent && i > 0 && !!chunkedSegments[i - 1].exponent;
  }

  return chunkedSegments;
});

// ── Seleção de texto ─────────────────────────────────────────────────────────
// Mapeia um Range do DOM para offsets absolutos no conteúdo, aparando espaços
// em branco nas extremidades. Retorna { startOffset, endOffset } ou null.
function mapRangeToContentOffsets(range) {
  if (!range || !containerRef.value) return null;

  let startOffset = null;
  let endOffset = null;

  const allNodes = containerRef.value.querySelectorAll("[data-offset]");
  const normalizedStart =
    range.startContainer.nodeType === 3
      ? range.startContainer.parentNode
      : range.startContainer;
  const normalizedEnd =
    range.endContainer.nodeType === 3
      ? range.endContainer.parentNode
      : range.endContainer;

  for (const node of allNodes) {
    const nodeStart = parseInt(node.getAttribute("data-offset"), 10);

    if (node.contains(normalizedStart) || node === normalizedStart) {
      if (startOffset === null) startOffset = nodeStart + range.startOffset;
    }
    if (node.contains(normalizedEnd) || node === normalizedEnd) {
      endOffset = nodeStart + range.endOffset;
    }
  }

  // Apara espaços em branco nas extremidades. Isso garante que uma seleção que
  // termine (ou comece) num espaço — caso típico do duplo clique numa palavra
  // seguida de espaço — não inclua esse espaço na nota emitida.
  if (startOffset != null && endOffset != null) {
    while (endOffset > startOffset && /\s/.test(props.content[endOffset - 1])) {
      endOffset -= 1;
    }
    while (startOffset < endOffset && /\s/.test(props.content[startOffset])) {
      startOffset += 1;
    }
  }

  if (startOffset != null && endOffset != null && startOffset < endOffset) {
    return { startOffset, endOffset };
  }
  return null;
}

// Emite selection-made com proteção contra emissão duplicada. O duplo clique
// dispara mouseup (handleSelection) e, logo em seguida, dblclick; sem essa
// guarda o mesmo trecho poderia abrir o formulário duas vezes.
let lastEmittedSelection = { start: null, end: null, at: 0 };

function emitSelectionMade(startOffset, endOffset) {
  const now = Date.now();
  if (
    lastEmittedSelection.start === startOffset &&
    lastEmittedSelection.end === endOffset &&
    now - lastEmittedSelection.at < 500
  ) {
    return;
  }
  lastEmittedSelection = { start: startOffset, end: endOffset, at: now };

  emit("selection-made", {
    offsetStart: startOffset,
    offsetEnd: endOffset,
    selectedText: props.content.slice(startOffset, endOffset),
  });
}

function handleSelection(event) {
  if (event?.button !== LEFT_BUTTON) {
    resetLeftButtonSelection();
    return;
  }

  const wasLeftButtonDrag = leftButtonSelection.value.moved;
  resetLeftButtonSelection();

  // O componente de anotação só deve abrir quando a seleção nasce de arrasto
  // com o botão esquerdo. Clique simples ou mouseup direito não emitem nada.
  if (!wasLeftButtonDrag) return;

  // Quando a seleção foi feita com botão direito arrastado,
  // mantemos apenas a seleção visual e NÃO emitimos selection-made.
  // Isso impede a abertura do formulário/componente de anotação.
  if (suppressNextSelectionMade.value) {
    clearSelectionMadeSuppression();
    return;
  }

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  if (!containerRef.value?.contains(range.commonAncestorContainer)) return;

  const selectedText = selection.toString();
  if (!selectedText || selectedText.trim() === "") return;

  const trimmed = selectedText.replace(/\n+$/g, "");
  if (!trimmed) return;

  const offsets = mapRangeToContentOffsets(range);
  if (!offsets) return;

  const actualSelectedText = props.content.slice(
    offsets.startOffset,
    offsets.endOffset
  );
  if (actualSelectedText.trim() === trimmed.trim()) {
    emitSelectionMade(offsets.startOffset, offsets.endOffset);
  }
}

// ── Duplo clique: encolhe a seleção nativa para não pegar o espaço final ─────
// Ao dar duplo clique numa palavra seguida de espaço, o navegador costuma
// incluir o espaço em branco no fim da seleção. Aqui ajustamos o range para
// que a seleção termine exatamente no último caractere da palavra (também
// aparamos eventuais espaços à esquerda, por segurança).
function handleWordDoubleClick() {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;

  const range = selection.getRangeAt(0).cloneRange();
  if (!containerRef.value?.contains(range.commonAncestorContainer)) return;

  let changed = false;
  let guard = 0;

  // Apara espaços em branco à direita.
  while (guard < 256) {
    guard += 1;
    const { endContainer, endOffset } = range;
    if (
      endContainer.nodeType === Node.TEXT_NODE &&
      endOffset > 0 &&
      /\s/.test(endContainer.data[endOffset - 1])
    ) {
      range.setEnd(endContainer, endOffset - 1);
      changed = true;
      continue;
    }
    break;
  }

  // Apara espaços em branco à esquerda.
  guard = 0;
  while (guard < 256) {
    guard += 1;
    const { startContainer, startOffset } = range;
    if (
      startContainer.nodeType === Node.TEXT_NODE &&
      startOffset < startContainer.data.length &&
      /\s/.test(startContainer.data[startOffset])
    ) {
      range.setStart(startContainer, startOffset + 1);
      changed = true;
      continue;
    }
    break;
  }

  // Reaplica a seleção ajustada quando algo mudou e ela não colapsou (duplo
  // clique sobre um espaço, em que encolher tudo deixaria a seleção vazia —
  // nesse caso mantemos o comportamento original do navegador e não emitimos).
  if (changed && !range.collapsed) {
    selection.removeAllRanges();
    selection.addRange(range);
  }

  if (range.collapsed) return;

  // Abre o formulário de anotação para a palavra selecionada. handleSelection
  // (no mouseup) não emite em duplo clique por não haver arrasto; é aqui que a
  // seleção por duplo clique vira uma anotação.
  const offsets = mapRangeToContentOffsets(range);
  if (offsets) {
    emitSelectionMade(offsets.startOffset, offsets.endOffset);
  }
}

// ── Fluxo 2: highlight do trecho via highlightTextNoteId ─────────────────────
watch(
  () => noteStore.highlightTextNoteId,
  async (noteId) => {
    if (!noteId) return;

    const note = noteStore.allNotes.find((n) => n.noteId === noteId);
    if (!note) {
      noteStore.highlightTextNoteId = null;
      return;
    }

    const start = note.textRangeStart ?? note.textRangeStar ?? null;
    const end = note.textRangeEnd ?? null;
    if (start === 0 && end === 0) {
      noteStore.highlightTextNoteId = null;
      return;
    }

    await nextTick();

    if (!containerRef.value) {
      noteStore.highlightTextNoteId = null;
      return;
    }

    const allSpans = containerRef.value.querySelectorAll("[data-offset]");
    const matchingSpans = [];
    for (const span of allSpans) {
      const spanStart = parseInt(span.getAttribute("data-offset"), 10);
      const rawText = span.textContent ?? "";
      if (rawText.trim() === "") continue;
      const spanLen = rawText.length;
      const spanEnd = spanStart + spanLen;
      if (spanStart < end && spanEnd > start) matchingSpans.push(span);
    }

    if (matchingSpans.length === 0) {
      noteStore.highlightTextNoteId = null;
      return;
    }

    const firstSpan = matchingSpans[0];
    const scrollContainer = containerRef.value.closest(
      ".text-scroll-container"
    );
    if (scrollContainer) {
      const spanTop = firstSpan.getBoundingClientRect().top;
      const containerTop = scrollContainer.getBoundingClientRect().top;
      scrollContainer.scrollBy({
        top: spanTop - containerTop - 80,
        behavior: "smooth",
      });
    } else {
      firstSpan.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    const allSameParent = matchingSpans.every(
      (s) => s.parentNode === matchingSpans[0].parentNode
    );

    if (allSameParent && matchingSpans.length > 0) {
      const parent = matchingSpans[0].parentNode;
      const anchor = matchingSpans[0];

      const wrapper = document.createElement("mark");
      wrapper.className = "note-highlight-flash";
      wrapper.style.cssText =
        "background: none; padding: 0; margin: 0; display: inline;";

      parent.insertBefore(wrapper, anchor);
      matchingSpans.forEach((s) => wrapper.appendChild(s));

      setTimeout(() => {
        matchingSpans.forEach((s) => parent.insertBefore(s, wrapper));
        wrapper.remove();
        noteStore.highlightTextNoteId = null;
      }, 4000);
    } else {
      matchingSpans.forEach((span) =>
        span.classList.add("note-highlight-flash")
      );
      setTimeout(() => {
        matchingSpans.forEach((span) =>
          span.classList.remove("note-highlight-flash")
        );
        noteStore.highlightTextNoteId = null;
      }, 4000);
    }
  }
);

onMounted(() => {
  scrollAnchorEl.value =
    containerRef.value?.closest(".text-scroll-container") ?? null;

  document.addEventListener("mousemove", handleLeftButtonMouseMove, true);
  document.addEventListener("mousemove", handleRightButtonMouseMove, true);
  document.addEventListener("mouseup", handleRightButtonMouseUp, true);
  document.addEventListener("contextmenu", handleGlobalContextMenu, true);
  document.addEventListener("mouseup", handleSelection);
  document.addEventListener("click", handleGlobalClick);
  document.addEventListener("keydown", handleGlobalKeydown);
});

onUnmounted(() => {
  document.removeEventListener("mousemove", handleLeftButtonMouseMove, true);
  document.removeEventListener("mousemove", handleRightButtonMouseMove, true);
  document.removeEventListener("mouseup", handleRightButtonMouseUp, true);
  document.removeEventListener("contextmenu", handleGlobalContextMenu, true);
  document.removeEventListener("mouseup", handleSelection);
  document.removeEventListener("click", handleGlobalClick);
  document.removeEventListener("keydown", handleGlobalKeydown);

  if (releaseContextMenuSuppressionTimer) {
    clearTimeout(releaseContextMenuSuppressionTimer);
  }

  if (releaseSelectionMadeSuppressionTimer) {
    clearTimeout(releaseSelectionMadeSuppressionTimer);
  }
});
</script>

<template>
  <span
    ref="containerRef"
    @mousedown="handleTextMouseDown"
    @dblclick="handleWordDoubleClick"
  >
    <template v-for="(seg, index) in highlightedSegments" :key="index">
      <span
        :data-offset="seg.start"
        :data-note-ids="seg.noteIdsAttr"
        :class="seg.classList"
        :style="seg.style"
        @click="props.hasHighlights && seg.hasNotes && onSegmentClick(seg.notes)"
        @contextmenu="
          props.hasHighlights &&
            seg.hasNotes &&
            onNoteRightClick(seg.notes, $event)
        "
        v-html="escapeHtml(seg.text)"
      />
      <sup
        v-if="seg.exponent && props.hasExponent"
        class="note-exponent text-xs font-bold"
        @click="onNoteClick(seg.notes)"
        @contextmenu="onNoteRightClick(seg.notes, $event)"
      >{{ seg.exponentIsContinuation ? ', ' : '' }}{{ seg.exponent }}</sup>
    </template>
  </span>

  <!-- ── Pilha flutuante com os criadores das notas visíveis ─────────────── -->
  <!-- Teleporta para body por conta própria; não interfere no fluxo do texto. -->
  <NoteCreatorsStack :notes="props.notes" :anchor="scrollAnchorEl" />

  <!-- ── Menu de contexto flutuante ──────────────────────────────────────── -->
  <Teleport to="body">
    <div
      v-if="contextMenu.visible"
      id="twn-context-menu"
      class="twn-context-menu"
      :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
      @click.stop
    >
      <!-- Editar nota (habilitado) -->
      <button
        v-if="contextMenu.canEditNote && contextMenu.noteId"
        type="button"
        class="twn-menu-item"
        @click="handleContextMenuEditNote"
      >
        <i class="fas fa-pencil-alt twn-menu-icon"></i>
        <span>Editar nota</span>
      </button>

      <!-- Editar nota (desabilitado) -->
      <div
        v-if="!contextMenu.canEditNote && contextMenu.noteId"
        class="twn-menu-item twn-menu-item--disabled"
        title="Sem permissão para editar esta nota"
      >
        <i class="fas fa-pencil-alt twn-menu-icon"></i>
        <span>Editar nota</span>
      </div>

      <!-- Detalhes da nota (sempre visível) -->
      <button
        type="button"
        class="twn-menu-item"
        @click="handleContextMenuDetails"
      >
        <i class="fas fa-file-alt twn-menu-icon"></i>
        <span>Detalhes da nota</span>
      </button>

      <!-- Apagar nota (terceira posição) -->
      <button
        v-if="contextMenu.canDeleteNote && contextMenu.noteId"
        type="button"
        class="twn-menu-item twn-menu-item--danger"
        @click="handleContextMenuDeleteNote"
      >
        <i class="fas fa-trash-alt twn-menu-icon"></i>
        <span>Apagar nota</span>
      </button>

      <!-- Separador entre ações destrutivas e demais -->
      <div class="twn-menu-separator"></div>

      <!-- Editar tag (habilitado) -->
      <button
        v-if="contextMenu.canEditTag && contextMenu.tagId"
        type="button"
        class="twn-menu-item"
        @click="handleContextMenuEditTag"
      >
        <i class="fas fa-pencil-alt twn-menu-icon"></i>
        <span>Editar tag</span>
      </button>

      <!-- Editar tag (desabilitado) -->
      <div
        v-if="!contextMenu.canEditTag || !contextMenu.tagId"
        class="twn-menu-item twn-menu-item--disabled"
        :title="!contextMenu.tagId ? 'Nota sem tag' : 'Sem permissão para editar esta tag'"
      >
        <i class="fas fa-pencil-alt twn-menu-icon"></i>
        <span>Editar tag</span>
      </div>

      <!-- Editar preferências (só quando não há tag e usuário pode editar a nota) -->
      <button
        v-if="!contextMenu.tagId && contextMenu.canEditNote"
        type="button"
        class="twn-menu-item"
        @click="handleContextMenuOpenSettings"
      >
        <i class="fas fa-sliders-h twn-menu-icon"></i>
        <span>Editar preferências</span>
      </button>
    </div>

    <!-- EditTagForm aberto pelo menu de contexto -->
    <EditTagForm
      v-if="showEditTagForm"
      @close="showEditTagForm = false"
      @refresh="handleEditTagRefresh"
    />

    <!-- ── NOVO: EditNoteForm aberto pelo menu de contexto ───────────────── -->
    <EditNoteForm
      v-if="showEditNoteForm && memberCanEditNote"
      :show="showEditNoteForm"
      :noteId="selectedNoteIdForEdit"
      :initialStep="1"
      @close="handleEditNoteClose"
    />

    <SettingsModal
      v-if="showSettingsModal"
      @close="showSettingsModal = false"
    />
  </Teleport>
</template>

<style scoped>
.note-exponent {
  vertical-align: 0.4em;
  cursor: pointer;
  color: var(--text-muted);
}

:global(.note-highlight-flash) {
  animation: text-flash 4s ease-out forwards;
  border-radius: 3px;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
  padding: 0 2px;
}

@keyframes text-flash {
  0% {
    background-color: var(--warning-bg);
    outline: 2px solid var(--warning);
    outline-offset: 1px;
  }
  60% {
    background-color: var(--warning-bg);
    outline: 2px solid var(--warning);
    outline-offset: 1px;
  }
  100% {
    background-color: transparent;
    outline: 2px solid transparent;
    outline-offset: 1px;
  }
}

/* ── Menu de contexto ───────────────────────────────────────────────────── */
:global(.twn-context-menu) {
  position: fixed;
  z-index: 9999;
  min-width: 150px;
  width: max-content;
  background-color: var(--surface);
  border-radius: 10px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-dropdown);
  overflow: hidden;
  user-select: none;
}

:global(.twn-menu-item) {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0.4rem 0.65rem;
  font-size: 0.875rem;
  line-height: 1.1;
  color: var(--text-dark);
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s ease, color 0.15s ease;
  white-space: nowrap;
}

:global(.twn-menu-item:hover) {
  background-color: var(--surface-hover);
}

:global(.twn-menu-item--disabled) {
  opacity: 0.38;
  cursor: not-allowed;
  pointer-events: none;
}

:global(.twn-menu-item--danger) {
  color: var(--text-important);
}

:global(.twn-menu-item--danger:hover) {
  background-color: var(--danger-hover-bg);
}

:global(.twn-menu-icon) {
  width: 16px;
  text-align: center;
  font-size: 0.9em;
  flex-shrink: 0;
}

:global(.twn-menu-separator) {
  height: 1px;
  background-color: var(--border-color);
  margin: 0;
}
</style>